import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// 分页组件
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  pageSizes?: number[];
  isPageSizeVisible?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  disabled = false,
  pageSize = 10,
  setPageSize,
  pageSizes = [10, 20, 50, 100],
  isPageSizeVisible = true
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isPageSizeDropdownVisible, setIsPageSizeDropdownVisible] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Handle dropdown positioning and update on scroll/resize
  useEffect(() => {
    function updateDropdownPosition() {
      if (isPageSizeDropdownVisible && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        let dropdownHeight = 0;
        if (dropdownMenuRef.current) {
          dropdownHeight = dropdownMenuRef.current.offsetHeight;
        } else {
          dropdownHeight = pageSizes.length * 28 + 16; // fallback estimate
        }
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        let top = rect.bottom + window.scrollY + 4;
        let transformOrigin = 'top';
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          // Not enough space below, but enough above: show above
          top = rect.top + window.scrollY - dropdownHeight - 4;
          transformOrigin = 'bottom';
        }
        setDropdownStyle({
          position: 'absolute',
          top,
          left: rect.left + window.scrollX,
          zIndex: 9999,
          minWidth: rect.width,
          transformOrigin,
        });
      }
    }
    if (isPageSizeDropdownVisible) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      // Recalculate after dropdown renders
      setTimeout(updateDropdownPosition, 0);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageSizeDropdownVisible, pageSizes.length]);

  // Hide dropdown on outside click
  useEffect(() => {
    if (!isPageSizeDropdownVisible) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(e.target as Node)
      ) {
        setIsPageSizeDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isPageSizeDropdownVisible]);

  // 确保安全的页数处理
  const safeTotalPages = Math.max(totalPages || 1, 1);
  const safeCurrentPage = Math.max(Math.min(currentPage, safeTotalPages), 1);

  const getVisiblePages = () => {
    const maxVisible = isMobile ? 5 : 7;
    const halfVisible = Math.floor(maxVisible / 2);
    
    let startPage = Math.max(1, safeCurrentPage - halfVisible);
    let endPage = Math.min(safeTotalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const visiblePages = getVisiblePages();

  const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
    <svg 
      className={`w-4 h-4 ${isMobile ? 'w-3 h-3' : ''}`} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} 
      />
    </svg>
  );

  return (
    <div className={`flex items-center justify-center space-x-1 ${isMobile ? '' : ''}`}>
      {/* Page size dropdown */}
      {isPageSizeVisible && setPageSize && (
        <div className="relative z-10 mr-2" ref={dropdownButtonRef}>
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setIsPageSizeDropdownVisible(!isPageSizeDropdownVisible)}
          >
            <div className="text-xs font-medium font-['Inter'] leading-none text-sel-normal dark:text-dark-sel-normal">
              {pageSize}
            </div>
            <div className="ml-2 text-xs flex items-baseline">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-3 w-3 translate-y-0.5 text-sel-normal dark:text-dark-sel-normal transition-transform duration-300 ${
                  isPageSizeDropdownVisible ? 'rotate-180' : ''
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          {/* Dropdown options rendered in portal */}
          {isPageSizeDropdownVisible && createPortal(
            <div
              ref={dropdownMenuRef}
              style={dropdownStyle}
              className={`px-2 py-1 bg-button dark:bg-dark-button rounded-md shadow-lg inline-flex flex-col justify-start items-start opacity-100 scale-y-100 transform max-h-48 overflow-y-auto`}
              onMouseDown={(e) => {
                // Prevent the outside click handler from closing the dropdown immediately
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {pageSizes.map((size) => (
                <div
                  key={size}
                  className={`self-stretch h-6 px-0.5 inline-flex justify-start items-center overflow-hidden cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover ${
                    size === pageSize ? 'text-sel-normal dark:text-dark-sel-normal' : 'text-normal dark:text-dark-normal'
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPageSize(size);
                    setIsPageSizeDropdownVisible(false);
                  }}
                >
                  <div className="justify-start text-xs font-normal font-['Inter'] leading-tight">
                    {size}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>
      )}

      {/* 上一页 */}
      <button
        onClick={() => onPageChange(safeCurrentPage - 1)}
        disabled={disabled || safeCurrentPage <= 1}
        className={`
          flex items-center justify-center 
          ${isMobile ? 'w-4 h-4 text-xs' : 'w-6 h-6 text-xs'}
          text-sel-normal dark:text-dark-sel-normal
          disabled:opacity-50 disabled:cursor-not-allowed
          hover:bg-gray-100 dark:hover:bg-gray-800
          transition-colors duration-200
          rounded
        `}
      >
        <ArrowIcon direction="left" />
      </button>

      {/* 首页省略号 */}
      {visiblePages[0] > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            disabled={disabled}
            className={`
              flex items-center justify-center
              ${isMobile ? 'w-4 h-4 text-xs' : 'w-6 h-6 text-xs'}
              text-normal dark:text-normal hover:text-reverse dark:hover:text-reverse
              hover:bg-gray-100 dark:hover:bg-gray-800
              transition-colors duration-200 rounded
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            1
          </button>
          {visiblePages[0] > 2 && (
            <span className="text-normal text-xs px-1">...</span>
          )}
        </>
      )}

      {/* 页码 */}
      {visiblePages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          disabled={disabled}
          className={`
            flex items-center justify-center
            ${isMobile ? 'w-4 h-4 text-xs' : 'w-6 h-6 text-xs'}
            transition-colors duration-200 rounded
            ${page === safeCurrentPage
              ? 'text-white font-medium'
              : 'text-normal dark:text-normal hover:text-reverse dark:hover:text-reverse hover:bg-gray-100 dark:hover:bg-gray-800'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {page}
        </button>
      ))}

      {/* 末页省略号 */}
      {visiblePages[visiblePages.length - 1] < safeTotalPages && (
        <>
          {visiblePages[visiblePages.length - 1] < safeTotalPages - 1 && (
            <span className="text-normal text-xs px-1">...</span>
          )}
          <button
            onClick={() => onPageChange(safeTotalPages)}
            disabled={disabled}
            className={`
              flex items-center justify-center
              ${isMobile ? 'w-4 h-4 text-xs' : 'w-6 h-6 text-xs'}
              text-normal dark:text-normal hover:text-reverse dark:hover:text-reverse
              hover:bg-gray-100 dark:hover:bg-gray-800
              transition-colors duration-200 rounded
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {safeTotalPages}
          </button>
        </>
      )}

      {/* 下一页 */}
      <button
        onClick={() => onPageChange(safeCurrentPage + 1)}
        disabled={disabled || safeCurrentPage >= safeTotalPages}
        className={`
          flex items-center justify-center
          ${isMobile ? 'w-4 h-4 text-xs' : 'w-6 h-6 text-xs'}
          text-sel-normal dark:text-dark-sel-normal
          disabled:opacity-50 disabled:cursor-not-allowed
          hover:bg-gray-100 dark:hover:bg-gray-800
          transition-colors duration-200
          rounded
        `}
      >
        <ArrowIcon direction="right" />
      </button>
    </div>
  );
};